import {
  ProductionRequirementRowType,
  SchedulerPackagingRoutesRowType,
  WorkstationDownTimeRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
} from './Scheduler.types'

// ─── Types ──────────────────────────────────────────────────────────

export type ValidationSeverity = 'error' | 'warning' | 'info'

export type ValidationResult = {
  id: string
  severity: ValidationSeverity
  message: string
  detail?: string
}

// ─── Helpers ────────────────────────────────────────────────────────

function parseDowntimeIntervals(
  s: string | null | undefined
): [number, number][] {
  if (!s) return []
  const intervals: [number, number][] = []
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null) {
    intervals.push([Math.round(parseFloat(m[1])), Math.round(parseFloat(m[2]))])
  }
  return intervals
}

// ─── Validators ─────────────────────────────────────────────────────

export function validateProductionRequirements(
  data: ProductionRequirementRowType[] | undefined
): ValidationResult[] {
  const results: ValidationResult[] = []

  if (!data || data.length === 0) {
    results.push({
      id: 'prod-req-empty',
      severity: 'error',
      message: 'No production requirements configured',
      detail:
        'At least one production requirement is needed to run the scheduler.',
    })
    return results
  }

  // Check for zero-quantity items
  const zeroQty = data.filter(
    (r) => !r.quantity || parseFloat(String(r.quantity)) <= 0
  )
  if (zeroQty.length > 0) {
    results.push({
      id: 'prod-req-zero-qty',
      severity: 'warning',
      message: `${zeroQty.length} product${zeroQty.length !== 1 ? 's' : ''} with zero or missing quantity`,
      detail: zeroQty.map((r) => r.productCode).join(', '),
    })
  }

  // Check for duplicate product codes
  const codeCount = new Map<string, number>()
  data.forEach((r) =>
    codeCount.set(r.productCode, (codeCount.get(r.productCode) ?? 0) + 1)
  )
  const duplicates = [...codeCount.entries()].filter(([, count]) => count > 1)
  if (duplicates.length > 0) {
    results.push({
      id: 'prod-req-duplicates',
      severity: 'warning',
      message: `${duplicates.length} duplicate product code${duplicates.length !== 1 ? 's' : ''}`,
      detail: duplicates
        .map(([code, count]) => `${code} (${count}x)`)
        .join(', '),
    })
  }

  return results
}

export function validatePackagingRoutes(
  routes: SchedulerPackagingRoutesRowType[] | undefined,
  productionReqs: ProductionRequirementRowType[] | undefined
): ValidationResult[] {
  const results: ValidationResult[] = []

  if (!routes || routes.length === 0) {
    results.push({
      id: 'routes-empty',
      severity: 'error',
      message: 'No packaging routes configured',
      detail:
        'The scheduler needs packaging routes to assign products to lines.',
    })
    return results
  }

  if (!productionReqs || productionReqs.length === 0) return results

  // Check for orphan products (in requirements but no route)
  const routeProducts = new Set(routes.map((r) => String(r.productCode)))
  const orphans = productionReqs.filter(
    (req) => !routeProducts.has(String(req.productCode))
  )

  if (orphans.length > 0) {
    results.push({
      id: 'routes-orphan-products',
      severity: 'warning',
      message: `${orphans.length} product${orphans.length !== 1 ? 's' : ''} have no packaging route`,
      detail: orphans.map((r) => r.productCode).join(', '),
    })
  }

  // Check for routes with no kitchen assigned
  const noKitchen = routes.filter((r) => !r.kitchen)
  if (noKitchen.length > 0) {
    results.push({
      id: 'routes-no-kitchen',
      severity: 'warning',
      message: `${noKitchen.length} route${noKitchen.length !== 1 ? 's' : ''} missing kitchen assignment`,
      detail: noKitchen.map((r) => r.productCode).join(', '),
    })
  }

  return results
}

export function validateDowntime(
  downtime: WorkstationDownTimeRowType[] | undefined
): ValidationResult[] {
  const results: ValidationResult[] = []

  if (!downtime || downtime.length === 0) {
    results.push({
      id: 'downtime-none',
      severity: 'info',
      message: 'No downtime configured',
      detail: 'All resources will be available for the full 168-hour week.',
    })
    return results
  }

  // Check per-resource total downtime
  const resourceDowntime = new Map<string, number>()
  for (const row of downtime) {
    const intervals = parseDowntimeIntervals(row.downTime)
    const total = intervals.reduce(
      (sum, [start, end]) => sum + (end - start),
      0
    )
    const current = resourceDowntime.get(row.resource) ?? 0
    resourceDowntime.set(row.resource, current + total)
  }

  for (const [resource, hours] of resourceDowntime) {
    if (hours >= 168) {
      results.push({
        id: `downtime-full-${resource}`,
        severity: 'warning',
        message: `${resource} has ${hours}h downtime (entire week)`,
        detail: 'This resource will have no available production time.',
      })
    } else if (hours >= 120) {
      results.push({
        id: `downtime-high-${resource}`,
        severity: 'info',
        message: `${resource} has ${hours}h downtime (${Math.round((hours / 168) * 100)}% of week)`,
      })
    }
  }

  return results
}

export function validateConstraints(
  constraints: SchedulerResourceAndMaterialConstraintsRowType[] | undefined
): ValidationResult[] {
  const results: ValidationResult[] = []

  if (!constraints || constraints.length === 0) return results

  const invalid = constraints.filter((c) => c.ConstraintAmount <= 0)
  if (invalid.length > 0) {
    results.push({
      id: 'constraints-invalid',
      severity: 'warning',
      message: `${invalid.length} constraint${invalid.length !== 1 ? 's' : ''} with zero or negative amount`,
      detail: invalid
        .map((c) => `${c.ResourceConstraint}: ${c.ConstraintAmount}`)
        .join(', '),
    })
  }

  return results
}

// ─── Run All Validations ────────────────────────────────────────────

export type ValidationInputs = {
  productionRequirements?: ProductionRequirementRowType[]
  packagingRoutes?: SchedulerPackagingRoutesRowType[]
  downtime?: WorkstationDownTimeRowType[]
  constraints?: SchedulerResourceAndMaterialConstraintsRowType[]
  dataModifiedSinceRun?: boolean
}

export function runAllValidations(
  inputs: ValidationInputs
): ValidationResult[] {
  const results: ValidationResult[] = []

  results.push(...validateProductionRequirements(inputs.productionRequirements))
  results.push(
    ...validatePackagingRoutes(
      inputs.packagingRoutes,
      inputs.productionRequirements
    )
  )
  results.push(...validateDowntime(inputs.downtime))
  results.push(...validateConstraints(inputs.constraints))

  if (inputs.dataModifiedSinceRun) {
    results.push({
      id: 'data-modified',
      severity: 'info',
      message: 'Data has been modified since the last scheduler run',
    })
  }

  return results
}

export function hasErrors(results: ValidationResult[]): boolean {
  return results.some((r) => r.severity === 'error')
}

export function countBySeverity(
  results: ValidationResult[]
): Record<ValidationSeverity, number> {
  return {
    error: results.filter((r) => r.severity === 'error').length,
    warning: results.filter((r) => r.severity === 'warning').length,
    info: results.filter((r) => r.severity === 'info').length,
  }
}

// ─── Engine notices ─────────────────────────────────────────────────

/**
 * Adapt the engine's preprocessing notices to the shape the validation panel
 * already renders, so run-time findings and pre-run data checks read the same
 * way rather than growing a second, divergent panel.
 *
 * The severity is decided in the engine (`scheduler::notices`), beside the
 * code that authors the messages — deliberately not re-derived here from the
 * message text, which would put the same classification in two places and let
 * them drift.
 */
export function noticesToValidationResults(
  notices: { severity: string; message: string }[] | undefined
): ValidationResult[] {
  if (!notices?.length) return []
  return notices.map((n, i) => ({
    id: `engine-notice-${i}`,
    severity: (['error', 'warning', 'info'].includes(n.severity)
      ? n.severity
      : 'warning') as ValidationSeverity,
    message: n.message,
  }))
}
